import { useEffect, useState } from "react";
import { Trash, ChatCircleText, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";

const EMPTY = { company: "", contact_name: "", email: "", phone: "", stage: "lead", next_step: "", next_step_date: "", est_seats: "", source: "", notes: "", lost_reason: "" };
const fmtAt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

/** Create / edit a prospect. `prospect` null → create. readOnly shows the admin's view (no edits, notes visible). */
export function ProspectDialog({ open, onClose, prospect, stages, sources, onSaved, readOnly = false }) {
  const [form, setForm] = useState(EMPTY);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setNote("");
    setForm(prospect ? { ...EMPTY, ...prospect, next_step_date: prospect.next_step_date || "", est_seats: prospect.est_seats ?? "" } : EMPTY);
  }, [open, prospect]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const payload = () => {
    const { company, contact_name, email, phone, stage, next_step, next_step_date, est_seats, source, notes, lost_reason } = form;
    return { company, contact_name, email, phone, stage, next_step, next_step_date: next_step_date || null, est_seats: est_seats === "" ? null : est_seats, source, notes, lost_reason };
  };
  const save = async () => {
    setBusy(true);
    try {
      const res = prospect ? await API.put(`/reseller-portal/prospects/${prospect.id}`, payload()) : await API.post("/reseller-portal/prospects", payload());
      toast.success(prospect ? "Prospect updated" : "Prospect added");
      onSaved(res.data); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save the prospect"); }
    setBusy(false);
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      const { data } = await API.post(`/reseller-portal/prospects/${prospect.id}/notes`, { text: note });
      setNote(""); setForm((f) => ({ ...f, activity: data.activity })); onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add the note"); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${prospect.company} from your pipeline?`)) return;
    try { await API.delete(`/reseller-portal/prospects/${prospect.id}`); toast.success("Prospect deleted"); onSaved(null, prospect.id); onClose(); } catch { toast.error("Couldn't delete"); }
  };
  const closedStage = form.stage === "won" || form.stage === "lost";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prospect-dialog">
        <DialogHeader>
          <DialogTitle>{prospect ? prospect.company : "Add a prospect"}</DialogTitle>
          <DialogDescription>{readOnly ? "Read-only view of the reseller's pipeline entry." : "Who you're talking to, where it's at and what happens next."}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label className="text-xs">Company *</Label><Input value={form.company} onChange={set("company")} disabled={readOnly} className="mt-1" data-testid="prospect-company-input" /></div>
          <div><Label className="text-xs">Contact name</Label><Input value={form.contact_name} onChange={set("contact_name")} disabled={readOnly} className="mt-1" data-testid="prospect-contact-input" /></div>
          <div><Label className="text-xs">E-mail</Label><Input type="email" value={form.email} onChange={set("email")} disabled={readOnly} className="mt-1" data-testid="prospect-email-input" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={set("phone")} disabled={readOnly} className="mt-1" data-testid="prospect-phone-input" /></div>
          <div>
            <Label className="text-xs">Stage</Label>
            <select value={form.stage} onChange={set("stage")} disabled={readOnly} className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="prospect-stage-select">
              {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Source</Label>
            <select value={form.source} onChange={set("source")} disabled={readOnly} className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="prospect-source-select">
              <option value="">—</option>
              {(sources || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {!closedStage && (
            <>
              <div><Label className="text-xs">Next step</Label><Input value={form.next_step} onChange={set("next_step")} disabled={readOnly} placeholder="e.g. Send proposal" className="mt-1" data-testid="prospect-next-step-input" /></div>
              <div><Label className="text-xs">Next step date</Label><Input type="date" value={form.next_step_date} onChange={set("next_step_date")} disabled={readOnly} className="mt-1" data-testid="prospect-next-date-input" /></div>
            </>
          )}
          {form.stage === "lost" && <div className="sm:col-span-2"><Label className="text-xs">Why lost?</Label><Input value={form.lost_reason} onChange={set("lost_reason")} disabled={readOnly} className="mt-1" data-testid="prospect-lost-reason-input" /></div>}
          <div><Label className="text-xs">Estimated seats</Label><Input type="number" min="1" max="5000" value={form.est_seats} onChange={set("est_seats")} disabled={readOnly} className="mt-1" data-testid="prospect-seats-input" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={set("notes")} disabled={readOnly} rows={2} className="mt-1" data-testid="prospect-notes-input" /></div>
        </div>

        {prospect && (
          <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2" data-testid="prospect-activity">
            <p className="text-xs font-semibold flex items-center gap-1"><ChatCircleText size={14} /> Activity</p>
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {[...(form.activity || [])].reverse().map((a, i) => (
                <li key={i} className="text-xs flex gap-2" data-testid="prospect-activity-item">
                  <span className="text-muted-foreground whitespace-nowrap">{fmtAt(a.at)}</span>
                  <span className="flex items-center gap-1">{a.kind === "won" && <CheckCircle size={12} className="text-emerald-600" />}{a.kind === "stage" && a.text.startsWith("Moved to Lost") && <XCircle size={12} className="text-red-500" />}{a.text}{a.by ? <span className="text-muted-foreground"> — {a.by}</span> : null}</span>
                </li>
              ))}
            </ul>
            {!readOnly && (
              <div className="flex gap-2">
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid="prospect-note-input" />
                <Button size="sm" variant="outline" onClick={addNote} disabled={busy || !note.trim()} data-testid="prospect-note-add">Add</Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {prospect && !readOnly && <Button variant="ghost" size="sm" className="mr-auto text-red-600" onClick={remove} data-testid="prospect-delete"><Trash size={14} className="mr-1" /> Delete</Button>}
          <Button variant="outline" onClick={onClose}>{readOnly ? "Close" : "Cancel"}</Button>
          {!readOnly && <Button onClick={save} disabled={busy || !form.company.trim()} data-testid="prospect-save">{busy ? "Saving…" : prospect ? "Save changes" : "Add prospect"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
